/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Fab from "@mui/material/Fab";
import Collapse from "@mui/material/Collapse";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import KeyboardArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardArrowLeftOutlined";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import SidenavCollapse from "components/Sidenav/SidenavCollapse";
import SidenavRoot from "components/Sidenav/SidenavRoot";
import sidenavLogoLabel from "components/Sidenav/styles/sidenav";
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";
import { Grid } from "@mui/material";
import Authorize from "../Authorize";
import { ModeAuthorizeEnum } from "utils/constants";

function renderNestedItems({
  items,
  activeCollapse,
  setActiveCollapse,
  openCollapse,
  setOpenCollapse,
  handleToClickItemMenu,
  collapseName,
  textColor,
  level = 0,
}) {
  return items?.map((item, index) => {
    if (item.hiding || (item.access !== 0 && item.access !== 1)) return null;

    const isOpen = openCollapse[item.key] || false;
    const isActive = collapseName.includes(item.key);

    const paddingLeft = `${level * 16}px`;

    // Kiểm tra nếu có collapse và tất cả các phần tử con đều có hiding: true
    const allChildrenHidden =
      item.collapse?.length > 0 && item.collapse.every((child) => child.hiding);

    if (item.collapse?.length > 0 && !allChildrenHidden) {
      return (
        <Authorize
          key={item.key + index}
          mode={ModeAuthorizeEnum.view}
          permission={item.key}
        >
          <>
            <NavLink
              to={item.route || "#"}
              onClick={(e) => {
                e.preventDefault();
                setActiveCollapse(item.key);
                setOpenCollapse((prev) => ({
                  ...prev,
                  [item.key]: !prev[item.key],
                }));
              }}
            >
              <SidenavCollapse
                name={item.name}
                icon={item.icon || <Icon>dashboard</Icon>}
                active={isActive}
                iconRight={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                style={{ marginLeft: paddingLeft }}
              />
            </NavLink>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              {renderNestedItems({
                items: item.collapse,
                activeCollapse,
                setActiveCollapse,
                openCollapse,
                setOpenCollapse,
                handleToClickItemMenu,
                collapseName,
                textColor,
                level: level + 1,
              })}
            </Collapse>
          </>
        </Authorize>
      );
    }

    // Xử lý như route cấp 2 bình thường nếu không có collapse hoặc tất cả con đều hidden
    return (
      <Authorize
        key={item.key + index}
        mode={ModeAuthorizeEnum.view}
        permission={item.key}
      >
        <NavLink to={item.route || "#"} onClick={handleToClickItemMenu}>
          <SidenavCollapse
            name={item.name}
            icon={item.icon || <Icon>dashboard</Icon>}
            active={item.key === collapseName}
            style={{ marginLeft: paddingLeft }}
          />
        </NavLink>
      </Authorize>
    );
  });
}

function Sidenav({ color = "info", brand = "", brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } =
    controller;
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");
  const [activeCollapse, setActiveCollapse] = useState(
    routes.find((r) => r.route === location.pathname)?.key || null
  );
  const [openCollapse, setOpenCollapse] = useState({});

  let textColor = "white";
  if (transparentSidenav || (whiteSidenav && !darkMode)) textColor = "dark";
  else if (whiteSidenav && darkMode) textColor = "inherit";

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    function handleMiniSidenav() {
      setTransparentSidenav(
        dispatch,
        window.innerWidth >= 1200 && transparentSidenav
      );
      setWhiteSidenav(dispatch, window.innerWidth >= 1200 && whiteSidenav);
    }
    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, transparentSidenav, whiteSidenav]);

  useEffect(() => {
    const expandParents = (items, pathname, parents = []) => {
      for (const item of items) {
        if (item.route && pathname.startsWith(item.route))
          return [...parents, item.key];
        if (item.collapse?.length) {
          const result = expandParents(item.collapse, pathname, [
            ...parents,
            item.key,
          ]);
          if (result.length) return result;
        }
      }
      return [];
    };
    const keys = expandParents(routes, location.pathname);
    if (keys.length > 0) {
      const openMap = {};
      keys.forEach((k) => (openMap[k] = true));
      setOpenCollapse((prev) => ({ ...prev, ...openMap }));
      setActiveCollapse(keys[keys.length - 1]);
    }
  }, [location.pathname, routes]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleToClickItemMenu = () => setMiniSidenav(dispatch, true);

  const renderRoutes = renderNestedItems({
    items: routes,
    activeCollapse,
    setActiveCollapse,
    openCollapse,
    setOpenCollapse,
    handleToClickItemMenu,
    collapseName,
    textColor,
    level: 0,
  });

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox mt={3} mb={1} px={4} textAlign="center" p={0}>
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        />
        <MDBox
          component={NavLink}
          to="/dashboard"
          display="flex"
          alignItems="center"
          sx={{ marginLeft: miniSidenav ? "2rem" : 0 }}
        >
          {brand && (
            <MDBox component="img" src={brand} alt="Brand" width="2rem" />
          )}
          <MDBox
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
            style={{
              textAlign: "left",
              whiteSpace: "normal",
              display: miniSidenav ? "none" : "block",
            }}
          >
            <MDTypography
              component="h6"
              variant="button"
              fontWeight="medium"
              color={textColor}
              sx={{ letterSpacing: "0.02em" }}
            >
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <Grid item container direction="row" justifyContent="flex-end">
        <Fab
          size="small"
          sx={{
            color: "#fff",
            background: "linear-gradient(195deg, #3F51B5, #3f51b545)",
            position: "absolute",
            right: 0,
            lineHeight: "20px",
            borderRadius: "50%",
            top: "58px",
            cursor: "pointer",
          }}
          color="info"
          aria-label="add"
          onClick={handleMiniSidenav}
        >
          {miniSidenav ? (
            <KeyboardArrowRightOutlinedIcon />
          ) : (
            <KeyboardArrowLeftOutlinedIcon />
          )}
        </Fab>
      </Grid>
      <List>{renderRoutes}</List>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
    </SidenavRoot>
  );
}

Sidenav.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
